import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import RequestState from '../../../types/RequestState';
import ScrollReset from '../../common/ScrollReset';
import CheckboxField from '../../form/CheckboxField';
import FormGroupField from '../../form/FormGroupField';
import RecaptchaLegalBanner from '../../legal/RecaptchaLegalBanner';
import ErrorHandler from '../../loader/ErrorHandler';
import PrivacyStatementLink from '../../privacy-statement/PrivacyStatementLink';
import TermsOfUseStatementLink from '../../terms-of-use-statement/TermsOfUseStatementLink';
import messages from '../messages';

const formName = 'OrderCancellationForm';

const SummaryRow = ({ label, value }) => {
  return (
    <div className="OrderCancellationContent__summary-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
};

SummaryRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
};

const OrderCancellationContent = ({
  apiErrorMessages,
  cancelWholeOrder,
  configStore,
  currentStep,
  form,
  goToConfirmStep,
  goToDetailsStep,
  isOrderContextPrefilled,
  intl,
  orderContextLoadFailed,
  requestState,
  setCancellationScope,
  submit,
  submitErrorModel,
  submitRequestId,
  summary,
}) => {
  const isSubmitting = requestState === RequestState.LOADING;
  const isDetailsStepDisabled = isSubmitting;
  const showManualProductDescription = !cancelWholeOrder;

  const renderFieldError = (fieldName) => {
    const field = form.fields.get(fieldName);

    if (!field.errors.length) {
      return null;
    }

    return <FormFeedback className="d-block">{field.errors[0]}</FormFeedback>;
  };

  const renderScopeChoice = (value, messageDescriptor) => {
    const field = form.fields.get('cancellationScope');
    const checked = field.value === value;

    return (
      <FormGroup check key={value}>
        <Label check>
          <Input
            checked={checked}
            disabled={isDetailsStepDisabled}
            name="cancellationScope"
            onBlur={() => field.setShouldValidate(true)}
            onChange={() => setCancellationScope(value)}
            type="radio"
            value={value}
          />{' '}
          <FormattedMessage {...messageDescriptor} />
        </Label>
      </FormGroup>
    );
  };

  const renderDetailsStep = () => {
    return (
      <Form
        className="OrderCancellationContent__form"
        name={formName}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          goToConfirmStep();
        }}
      >
        {submitErrorModel && <ErrorHandler error={submitErrorModel} />}
        {orderContextLoadFailed && (
          <Alert color="warning">
            <FormattedMessage {...messages.loadOrderFallback} />
          </Alert>
        )}
        {isOrderContextPrefilled && (
          <Alert color="success">
            <FormattedMessage {...messages.loadOrderSuccess} />
          </Alert>
        )}
        {apiErrorMessages && (
          <Alert color="danger" key={submitRequestId}>
            {apiErrorMessages.map((message, index) => (
              <div key={`${message}-${index}`}>{message}</div>
            ))}
          </Alert>
        )}
        <Row>
          <Col md="6">
            <FormGroupField
              autoComplete="email"
              field={form.fields.get('email')}
              fieldName="email"
              formName={formName}
              label={intl.formatMessage(messages.emailLabel)}
              required
              type="email"
              disabled={isDetailsStepDisabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <FormGroupField
              inputMode="numeric"
              field={form.fields.get('orderIdentifier')}
              fieldName="orderIdentifier"
              formName={formName}
              label={intl.formatMessage(messages.orderIdentifierLabel)}
              pattern="[0-9]*"
              required
              disabled={isDetailsStepDisabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <fieldset className="OrderCancellationContent__fieldset">
              <legend>
                <FormattedMessage {...messages.cancellationScopeLabel} />
              </legend>
              {renderScopeChoice('whole', messages.cancelWholeOrderOption)}
              {renderScopeChoice('partial', messages.cancelPartOfOrderOption)}
              {renderFieldError('cancellationScope')}
            </fieldset>
          </Col>
        </Row>
        {showManualProductDescription && (
          <FormGroupField
            formGroupClassName="OrderCancellationContent__product-description"
            description={
              <div className="OrderCancellationContent__field-help">
                <FormattedMessage {...messages.productDescriptionHelp} />
              </div>
            }
            field={form.fields.get('productDescription')}
            fieldName="productDescription"
            formName={formName}
            label={intl.formatMessage(messages.productDescriptionLabel)}
            required
            rows="4"
            type="textarea"
            disabled={isDetailsStepDisabled}
          />
        )}
        <FormGroupField
          formGroupClassName="OrderCancellationContent__additional-message"
          field={form.fields.get('additionalMessage')}
          fieldName="additionalMessage"
          formName={formName}
          label={intl.formatMessage(messages.additionalMessageLabel)}
          rows="5"
          type="textarea"
          disabled={isDetailsStepDisabled}
        />
        <CheckboxField
          className="OrderCancellationContent__privacy"
          required={form.fields.get('privacyStatement').hasValidators()}
          field={form.fields.get('privacyStatement')}
          formName={formName}
          fieldName={'privacyStatement'}
          options={{
            value: '1',
            label: (
              <FormattedMessage
                {...globalTranslations.privacyStatementAcceptSentence}
              />
            ),
          }}
        />
        <div className="OrderCancellationContent__privacy-link">
          (<PrivacyStatementLink />)
        </div>
        {configStore.recaptcha.enabled && <RecaptchaLegalBanner />}
        <Button color="primary" disabled={isDetailsStepDisabled} type="submit">
          <FormattedMessage {...messages.nextStep} />
        </Button>
      </Form>
    );
  };

  const renderConfirmStep = () => {
    return (
      <div className="OrderCancellationContent__confirm">
        {submitErrorModel && <ErrorHandler error={submitErrorModel} />}
        {apiErrorMessages && (
          <Alert color="danger" key={submitRequestId}>
            {apiErrorMessages.map((message, index) => (
              <div key={`${message}-${index}`}>{message}</div>
            ))}
          </Alert>
        )}
        <p className="OrderCancellationContent__notice">
          <FormattedMessage {...messages.confirmStepNotice} />
        </p>
        <dl className="OrderCancellationContent__summary">
          <SummaryRow
            label={<FormattedMessage {...messages.summaryEmail} />}
            value={summary.email}
          />
          <SummaryRow
            label={<FormattedMessage {...messages.summaryOrderIdentifier} />}
            value={summary.orderIdentifier}
          />
          <SummaryRow
            label={<FormattedMessage {...messages.summaryScope} />}
            value={summary.scope}
          />
          {!cancelWholeOrder && (
            <SummaryRow
              label={<FormattedMessage {...messages.summaryProducts} />}
              value={
                <ul className="OrderCancellationContent__summary-products">
                  {summary.products.map((product, index) => (
                    <li key={`${product}-${index}`}>{product}</li>
                  ))}
                </ul>
              }
            />
          )}
          {summary.additionalMessage && (
            <SummaryRow
              label={
                <FormattedMessage {...messages.summaryAdditionalMessage} />
              }
              value={summary.additionalMessage}
            />
          )}
        </dl>
        <div className="OrderCancellationContent__actions">
          <Button color="secondary" onClick={goToDetailsStep} type="button">
            <FormattedMessage {...messages.backToEdit} />
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={submit}
            type="button"
          >
            <FormattedMessage {...messages.confirmAndSend} />
          </Button>
        </div>
      </div>
    );
  };

  const renderSuccessStep = () => {
    return (
      <Alert color="success">
        <h2 className="h4">
          <FormattedMessage {...messages.successTitle} />
        </h2>
        <p className="mb-0">
          <FormattedMessage {...messages.successMessage} />
        </p>
      </Alert>
    );
  };

  return (
    <section className="OrderCancellationContent">
      <ScrollReset key={currentStep} />
      {currentStep === 'DETAILS' && (
        <>
          <p className="OrderCancellationContent__intro">
            <FormattedMessage {...messages.intro} />
            <span className="OrderCancellationContent__intro-link">
              (
              <TermsOfUseStatementLink>
                <FormattedMessage {...messages.termsOfDeliveryLinkLabel} />
              </TermsOfUseStatementLink>
              )
            </span>
          </p>
          {renderDetailsStep()}
        </>
      )}
      {currentStep === 'CONFIRM' && <>{renderConfirmStep()}</>}
      {currentStep === 'SUCCESS' && renderSuccessStep()}
    </section>
  );
};

OrderCancellationContent.propTypes = {
  apiErrorMessages: PropTypes.array,
  cancelWholeOrder: PropTypes.bool.isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  currentStep: PropTypes.string.isRequired,
  form: PropTypes.shape({
    fields: PropTypes.object.isRequired,
  }).isRequired,
  goToConfirmStep: PropTypes.func.isRequired,
  goToDetailsStep: PropTypes.func.isRequired,
  isOrderContextPrefilled: PropTypes.bool.isRequired,
  orderContextLoadFailed: PropTypes.bool.isRequired,
  requestState: PropTypes.string.isRequired,
  setCancellationScope: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  submitErrorModel: PropTypes.object,
  submitRequestId: PropTypes.number.isRequired,
  summary: PropTypes.shape({
    additionalMessage: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    orderIdentifier: PropTypes.string.isRequired,
    products: PropTypes.array.isRequired,
    scope: PropTypes.string.isRequired,
  }).isRequired,
};

export default injectIntl(
  inject('configStore')(observer(OrderCancellationContent))
);
